import { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import { getChat, streamMessage, createChat } from "../api/chats";
import { useToast } from "../context/ToastContext";

function SourceCitation({ sources }) {
  if (!sources?.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {sources.map((s, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-1 rounded-full bg-accent/10 px-2.5 py-0.5 text-xs text-accent border border-accent/20"
        >
          {s.file_name} — §{s.chunk_index}
        </span>
      ))}
    </div>
  );
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1 py-2 px-1">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-1.5 w-1.5 rounded-full bg-[#555] animate-bounce"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </div>
  );
}

function Message({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-5`}>
      {isUser ? (
        <div className="max-w-[70%] rounded-2xl rounded-tr-sm bg-[#16161A] px-4 py-2.5 text-sm text-[#e5e5e5]">
          {msg.content}
        </div>
      ) : (
        <div className="max-w-full">
          <div className="prose prose-sm prose-invert max-w-none text-[#e5e5e5] text-sm leading-relaxed">
            <ReactMarkdown>{msg.content}</ReactMarkdown>
          </div>
          <SourceCitation sources={msg.sources} />
        </div>
      )}
    </div>
  );
}

function StreamingMessage({ content, sources }) {
  return (
    <div className="flex justify-start mb-5">
      <div className="max-w-full">
        {content ? (
          <>
            <div className="prose prose-sm prose-invert max-w-none text-[#e5e5e5] text-sm leading-relaxed">
              <ReactMarkdown>{content}</ReactMarkdown>
            </div>
            <SourceCitation sources={sources} />
          </>
        ) : (
          <TypingDots />
        )}
      </div>
    </div>
  );
}

const REVIEW_SECTIONS = [
  { icon: "document-text-outline", label: "Overview", desc: "High-level summary of the document" },
  { icon: "list-outline", label: "Key Points", desc: "Important points and findings" },
  { icon: "flag-outline", label: "Issues & Errors", desc: "Problems, inconsistencies, and risks" },
  { icon: "create-outline", label: "Writing Quality", desc: "Clarity, structure, and readability" },
  { icon: "checkmark-circle-outline", label: "Completeness", desc: "Missing elements and gaps" },
];

function ReviewLaunchPanel({ onStart, streaming }) {
  const [notes, setNotes] = useState("");

  return (
    <div className="flex flex-1 items-center justify-center px-6 py-8 overflow-y-auto">
      <div className="w-full max-w-[560px]">
        <div className="mb-6">
          <h2 className="text-[#e5e5e5] text-base font-semibold mb-1">Full Document Review</h2>
          <p className="text-[#555] text-sm">
            Select a document from the sidebar, then run the analysis. The report will cover:
          </p>
        </div>

        <div className="flex flex-col gap-2 mb-6">
          {REVIEW_SECTIONS.map((s) => (
            <div
              key={s.label}
              className="flex items-center gap-3 rounded-lg border border-[#222] bg-[#1a1a1e] px-4 py-3"
            >
              <ion-icon name={s.icon} style={{ fontSize: "15px", color: "#555", flexShrink: 0 }} />
              <div className="flex items-baseline gap-2">
                <span className="text-xs font-medium text-[#ccc]">{s.label}</span>
                <span className="text-xs text-[#444]">{s.desc}</span>
              </div>
            </div>
          ))}
        </div>

        <textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional: Add focus notes or specific areas to pay attention to…"
          className="w-full resize-none bg-[#1e1e22] border border-[#2a2a2e] rounded-lg px-4 py-3 text-sm text-[#e5e5e5] placeholder-[#444] outline-none mb-4 focus:border-[#3a3a3e] transition-colors"
          style={{ maxHeight: "80px" }}
          disabled={streaming}
        />

        <button
          onClick={() => onStart(notes.trim())}
          disabled={streaming}
          className="w-full py-3 bg-[#e5e5e5] text-[#1a1a1a] text-sm font-semibold rounded-lg hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {streaming ? (
            <>
              <div className="h-3.5 w-3.5 rounded-full border-2 border-[#1a1a1a] border-t-transparent animate-spin" />
              Analyzing document…
            </>
          ) : (
            <>
              <ion-icon name="analytics-outline" style={{ fontSize: "16px" }} />
              Analyze Document
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default function ChatInterface({ chatId, selectedFileIds, mode, onChatCreated, onRequestUpload, onOpenAttachLink, onChatNotFound }) {
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamContent, setStreamContent] = useState("");
  const [streamSources, setStreamSources] = useState([]);
  const [pendingUserMsg, setPendingUserMsg] = useState(null);
  const bottomRef = useRef(null);
  const abortRef = useRef(null);
  const { toast } = useToast();
  const qc = useQueryClient();

  const { data: chatData, error: chatError } = useQuery({
    queryKey: ["chat", chatId],
    queryFn: async () => {
      const res = await getChat(chatId);
      return res.data.data;
    },
    enabled: !!chatId,
    refetchInterval: streaming ? false : undefined,
    refetchOnWindowFocus: !streaming,
    retry: (count, err) => err?.response?.status !== 404,
  });

  const messages = chatData?.messages || [];

  useEffect(() => {
    if (chatError?.response?.status === 404) {
      localStorage.removeItem("docmind:active_chat_id");
      onChatNotFound?.();
    }
  }, [chatError, onChatNotFound]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamContent, streaming]);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, [chatId]);

  const handleSend = async (overrideContent) => {
    const content = overrideContent !== undefined ? overrideContent : input.trim();
    if (!content || streaming) return;
    if (!selectedFileIds.length) {
      toast(
        mode === "review"
          ? "Please select at least one document you want to review"
          : "Please select at least one document to ask a question about",
        "warning"
      );
      return;
    }

    // Create a chat if none is active
    let activeChatId = chatId;
    if (!activeChatId) {
      try {
        const res = await createChat(null);
        activeChatId = res.data.data.id;
        onChatCreated?.(activeChatId);
        qc.invalidateQueries({ queryKey: ["chats"] });
      } catch {
        toast("Could not create chat", "error");
        return;
      }
    }

    if (overrideContent === undefined) setInput("");
    setStreaming(true);
    setStreamContent("");
    setStreamSources([]);
    setPendingUserMsg(content);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await streamMessage(activeChatId, content, selectedFileIds, mode);

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const event = JSON.parse(line.slice(6));
            if (event.type === "token") {
              setStreamContent((prev) => prev + event.content);
            } else if (event.type === "sources") {
              setStreamSources(event.data || []);
            } else if (event.type === "error") {
              toast(event.content || "Stream error", "error");
            }
          } catch {
            // malformed JSON line
          }
        }
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        toast(err.message || "Failed to send message", "error");
        if (overrideContent === undefined) setInput(content);
      }
    } finally {
      setStreaming(false);
      setStreamContent("");
      setStreamSources([]);
      setPendingUserMsg(null);
      qc.invalidateQueries({ queryKey: ["chat", activeChatId] });
      qc.invalidateQueries({ queryKey: ["chats"] });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const autoResize = (e) => {
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 160) + "px";
  };

  // Review mode with no messages yet: show launch panel
  const showReviewPanel = mode === "review" && messages.length === 0 && !streaming && !pendingUserMsg;

  return (
    <div className="flex flex-1 flex-col h-full min-h-0">
      {showReviewPanel ? (
        <ReviewLaunchPanel
          onStart={(notes) => handleSend(notes || "Full document review")}
          streaming={streaming}
        />
      ) : (
        <>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            {messages.length === 0 && !streaming && (
              <div className="flex h-full items-center justify-center" />
            )}

            <div className="mx-auto w-full max-w-[80%] md:max-w-[75%]">
              {messages.map((msg) => (
                <Message key={msg.id} msg={msg} />
              ))}

              {pendingUserMsg && (
                <Message msg={{ id: "pending", role: "user", content: pendingUserMsg }} />
              )}

              {streaming && (
                <StreamingMessage content={streamContent} sources={streamSources} />
              )}
            </div>

            <div ref={bottomRef} />
          </div>

          {/* Input bar */}
          <div className="flex justify-center px-6 pb-5 pt-2">
            <div className="flex w-full max-w-[80%] md:max-w-[75%] items-end gap-3 rounded-full bg-[#2B2B30] border border-[#303035] px-4 py-2.5 focus-within:bg-[#2E2E33] transition-colors">
              <button
                type="button"
                onClick={() => onRequestUpload?.()}
                title="Upload a file"
                className="flex h-8 w-8 flex-shrink-0 items-center justify-center text-[#555] hover:text-[#888] transition-colors"
              >
                <ion-icon name="add-outline" style={{ fontSize: "20px" }} />
              </button>
              <button
                type="button"
                onClick={() => onOpenAttachLink?.()}
                title="Attach a link"
                className="flex h-8 w-8 flex-shrink-0 items-center justify-center text-[#555] hover:text-[#888] transition-colors"
              >
                <ion-icon name="link-outline" style={{ fontSize: "18px" }} />
              </button>

              <textarea
                rows={1}
                value={input}
                onChange={(e) => { setInput(e.target.value); autoResize(e); }}
                onKeyDown={handleKeyDown}
                placeholder={mode === "review" ? "Ask a follow-up question…" : "Ask DocMind…"}
                className="flex-1 resize-none bg-transparent text-sm text-[#e5e5e5] placeholder-[#555] outline-none leading-5 mb-1"
                style={{ maxHeight: "160px" }}
                disabled={streaming}
              />

              <div className="flex items-end gap-2 flex-shrink-0">
                <button className="flex h-8 w-8 items-center justify-center text-[#555] hover:text-[#888] transition-colors">
                  <ion-icon name="mic-outline" style={{ fontSize: "20px" }} />
                </button>

                {streaming ? (
                  <button
                    onClick={() => abortRef.current?.abort()}
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-[#333] text-[#e5e5e5] hover:bg-[#3f3f3f] transition-colors"
                    title="Stop"
                  >
                    <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 16 16">
                      <rect x="3" y="3" width="10" height="10" rx="1" />
                    </svg>
                  </button>
                ) : (
                  <button
                    onClick={() => handleSend()}
                    disabled={!input.trim()}
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-[#e5e5e5] text-[#1a1a1a] hover:bg-white transition-colors disabled:opacity-25 disabled:cursor-not-allowed"
                  >
                    <ion-icon name="arrow-up-outline" style={{ fontSize: "16px" }} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
